import Logger from '@owc-utils/common/Logger';
import { SeamlessCoreStore } from '@seamless/store';
import { Reducer } from 'redux';
import ensureDynamicImport from '@owc-utils/Shims/moduleShim';

declare type ConnectionType<T> = {
  name: string;
  connections: T;
};

declare type Listener = (state: any) => void;

declare type ListenerTree<T> = {
  [key: string]: ListenerNameSpace<T>;
};

declare type ListenerNameSpace<T> = {
  [key: string]: T[];
};

const listeners: ListenerTree<Listener> = {};

let store: any = null;

export const getStore = async (): Promise<SeamlessCoreStore> => {
  if (store) {
    return store;
  } else {
    const { initializeStore } = await ensureDynamicImport<any>('@seamless/store');
    store = initializeStore();
  }
  return store;
};

let parentConnectionClass: any = null;

// this abstraction was needed, since we need to have an async constructor to support importShim in iOS <16
// and we cannot directly extend seamlessConnection. getConnection is a proxy to get the connection instance
// and must be called instead of using the direct connection returned from the constructor.
export class ShimmedConnection<Dispatchers, State> {
  private namespace: string;
  private reducer?: Reducer<State>;
  private state: State;
  private dispatchers?: Dispatchers;
  private connection: any;

  constructor(namespace: string, initialState: State, reducer?: Reducer<State>, dispatchers?: Dispatchers) {
    this.namespace = namespace;
    this.reducer = reducer;
    this.state = initialState;
    this.dispatchers = dispatchers;
  }

  private async getParentClass() {
    if (parentConnectionClass) {
      return parentConnectionClass;
    }
    const { SeamlessConnection } = await ensureDynamicImport<any>('@seamless/store');
    parentConnectionClass = SeamlessConnection;

    return parentConnectionClass;
  }

  private async init() {
    const classInstance = await this.getParentClass();
    const instance = new classInstance(this.namespace);
    instance.getReducer = this.getReducer.bind(this);
    instance.initialState = this.initialState;
    instance.getPublicDispatchers = this.getPublicDispatchers.bind(this);
    return instance;
  }

  public async getConnection() {
    if (!this.connection) {
      this.connection = await this.init();
    }
    return this.connection;
  }

  public setReducer(reducer: Reducer<State>) {
    this.reducer = reducer;
  }

  public setDispatchers = (dispatchers: Dispatchers) => {
    this.dispatchers = dispatchers;
  };

  protected getReducer(): Reducer<State> | undefined {
    return this.reducer;
  }

  public getPublicDispatchers(): Dispatchers | undefined {
    return this.dispatchers;
  }

  public async getAction(action: any, payload: any) {
    return (await this.getConnection()).getAction(action, payload);
  }

  public async getActionType(action: string) {
    return (await this.getConnection()).getActionType(action);
  }

  protected get initialState(): State | undefined {
    return this.state;
  }
}

const initializedNamespaces = new Set(['OWC_COMPONENT_REGISTRY']);

const addConnection = async (connection: any): Promise<void> => {
  const store = await getStore();
  await store.addConnection(connection).catch((e: Error) => Logger.error(e));
};

/**
 * Proxy method to connect to the redux store, under given namespace
 * @param connection
 * @returns Public dispatchers
 */
export const connect = async (connection: ConnectionType<Record<string, Function>>) => {
  const store = await getStore();
  if (!initializedNamespaces.has(connection.name)) {
    initializedNamespaces.add(connection.name);
    await addConnection(connection);
  }
  return await store.getConnectionDispatchers(connection.name);
};

/**
 * Proxy method to subscribe to a given namespace, triggering the provided listener
 * @param namespace
 * @param listener
 * @throws if namespace is not yet registered
 */
export const subscribe = async (id: string, namespace: string, listener: any) => {
  if (!initializedNamespaces.has(namespace)) {
    throw new Error(`[${id}] Cannot subscribe to namespace. ${namespace} has not been registered yet`);
  }

  const item = listeners[id];
  const namespaceItem = item && item[namespace];

  if (!item) {
    const payload: ListenerNameSpace<Listener> = {};
    payload[namespace] = [listener];
    listeners[id] = payload;
  } else if (!namespaceItem) {
    item[namespace] = [listener];
  } else {
    namespaceItem.push(listener);
  }

  return (await getStore()).subscribe(namespace, listener);
};

/**
 * Proxy method to unsubscribe from a given namespace, triggering the provided listener
 * @param namespace
 * @param listener
 * @throws if namespace is not yet registered
 */
export const unsubscribe = async (id: string, namespace: string, listener: any) => {
  const store = await getStore();
  if (!initializedNamespaces.has(namespace)) {
    throw new Error(`[${id}] Cannot subscribe to namespace. ${namespace} has not been registered yet`);
  }
  return store.unsubscribe(namespace, listener);
};

/**
 * Components should import getConnection if they need to pass data to
 * the store, rather than connecting multiple times to same namespace
 *
 */
export const getConnection = async <T extends Record<string, Function>>(namespace: string) => {
  return (await getStore()).getConnectionDispatchers<T>(namespace);
};

export const handleDestroy = async (id: string) => {
  const store = await getStore();
  const item = listeners[id];
  if (item) {
    const namespaces = Object.keys(item);
    namespaces.forEach(namespace => {
      item[namespace].forEach(listener => {
        store.unsubscribe(namespace, listener);
      });
    });
  }
};
